import { useState } from 'react'
import type { AppState, Equipment, OnboardingStep } from '../../shared/types.ts'
import { api } from '../lib/api.ts'
import { SHOPPING_LABEL, STEP_TITLE } from '../lib/script.ts'
import { X } from 'lucide-react'
import { EQUIPMENT_KINDS, KIND_LABEL, cx } from '../lib/util.ts'
import { Sheet } from './Sheet.tsx'

// "heat" alone reads as oven temperature, so every level says spice outright.
const SPICE = ['no chilli', 'mild spice', 'medium spice', 'properly spicy', 'very spicy', 'as spicy as it comes']

/** Review what onboarding captured, adjust it, or redo any one topic. */
export function SetupSheet({ state, onState, onClose, onRedo }: {
  state: AppState
  onState: (patch: Partial<AppState>) => void
  onClose: () => void
  onRedo: (step: OnboardingStep) => void
}) {
  const { preferences: p, equipment } = state
  const [editing, setEditing] = useState<string | null>(null)

  const groups = EQUIPMENT_KINDS
    .map((k) => [k, equipment.filter((e) => e.kind === k)] as const)
    .filter(([, items]) => items.length > 0)

  const removeEquipment = async (id: string) => onState({ equipment: (await api.removeEquipment(id)).equipment })
  const noteEquipment = async (e: Equipment, notes: string) => {
    onState({ equipment: (await api.updateEquipment(e.id, { notes })).equipment })
    setEditing(null)
  }
  const dropFrom = async (key: 'allergies' | 'diets' | 'dislikes' | 'loves' | 'cuisines', value: string) => {
    const next = { ...p, [key]: p[key].filter((v) => v !== value) }
    onState({ preferences: await api.savePreferences(next) })
  }

  const Section = ({ step, children }: { step: OnboardingStep; children: React.ReactNode }) => (
    <section className="block">
      <div className="block-head">
        <h3>{STEP_TITLE[step]}</h3>
        <button className="btn btn-ghost" onClick={() => onRedo(step)}>Redo</button>
      </div>
      {children}
    </section>
  )

  const Tags = ({ label, values, onDrop, alert }: {
    label: string; values: string[]; onDrop: (v: string) => void; alert?: boolean
  }) => values.length === 0 ? null : (
    <div className="row">
      <span className="row-label">{label}</span>
      <div className="chips">
        {values.map((v) => (
          <span key={v} className={cx('pill', alert && 'alert')}>
            {v}
            <button onClick={() => onDrop(v)} aria-label={`Remove ${v}`}><X size={14} strokeWidth={2.6} /></button>
          </span>
        ))}
      </div>
    </div>
  )

  return (
    <Sheet title="Your setup" onClose={onClose}>
      <Section step="equipment">
        {equipment.length === 0 && <p className="muted">Nothing listed — every recipe will assume a knife and a pan.</p>}
        {groups.map(([kind, items]) => (
          <div className="row" key={kind}>
            <span className="row-label">{KIND_LABEL[kind]}</span>
            <div className="chips">
              {items.map((e) => (
                editing === e.id ? (
                  <input
                    key={e.id}
                    className="pill-input"
                    autoFocus
                    defaultValue={e.notes}
                    placeholder="quirk — runs hot, 3.5L…"
                    onBlur={(ev) => void noteEquipment(e, ev.target.value)}
                    onKeyDown={(ev) => {
                      if (ev.key === 'Enter') void noteEquipment(e, (ev.target as HTMLInputElement).value)
                      if (ev.key === 'Escape') setEditing(null)
                    }}
                  />
                ) : (
                  <span key={e.id} className="pill">
                    <button className="pill-main" onClick={() => setEditing(e.id)}>
                      {e.name}{e.notes && <i> · {e.notes}</i>}
                    </button>
                    <button onClick={() => void removeEquipment(e.id)} aria-label={`Remove ${e.name}`}><X size={14} strokeWidth={2.6} /></button>
                  </span>
                )
              ))}
            </div>
          </div>
        ))}
        <p className="muted small">Tap a name to add a quirk — "runs 20°C hot", "3.5L basket". Those go straight to the model.</p>
      </Section>

      <Section step="limits">
        <Tags label="Allergies" values={p.allergies} onDrop={(v) => void dropFrom('allergies', v)} alert />
        <Tags label="Diets" values={p.diets} onDrop={(v) => void dropFrom('diets', v)} />
        <Tags label="Avoids" values={p.dislikes} onDrop={(v) => void dropFrom('dislikes', v)} />
        {!p.allergies.length && !p.diets.length && !p.dislikes.length && <p className="muted">Nothing off limits.</p>}
      </Section>

      <Section step="taste">
        <Tags label="Loves" values={p.loves} onDrop={(v) => void dropFrom('loves', v)} />
        <Tags label="Cuisines" values={p.cuisines} onDrop={(v) => void dropFrom('cuisines', v)} />
        <div className="row">
          <span className="row-label">Usually</span>
          <p className="muted" style={{ margin: 0 }}>
            {SPICE[p.spiceLevel] ?? 'medium spice'} · {p.skill} · about {p.defaultMaxMinutes} min · for {p.defaultServings}
          </p>
        </div>
        {p.notes && <div className="row"><span className="row-label">Also</span><p className="muted" style={{ margin: 0 }}>{p.notes}</p></div>}
      </Section>

      <Section step="shopping">
        <p className="muted" style={{ margin: 0 }}>{SHOPPING_LABEL[p.shopping]}</p>
      </Section>
    </Sheet>
  )
}
